import { useEffect, useMemo, useState } from 'react';
import { ScoreDocument } from '../../components/Header/header.gen.ts';
import { BiddingView, ExamDifficultyLevelView } from '../../graphql/types.gen.ts';
import { compareTwoBids, compareTwoBidsWithoutAlert } from '../../helpers.ts';
import { useIsUserLoggedIn } from '../../helpers/useValidateToken.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import { MeDocument, useLastGroupQuery } from '../FormPage/LoginPage/loginPage.gen.ts';
import { GetPublicLessonWithHomeworkBySlugDocument } from '../Lessons/lessons.gen.ts';
import { IResult } from './Game';
import { useExamFromGroupLazyQuery, useExamResultsSaveMutation, usePublicGroupsLazyQuery } from './game.gen.ts';

const RECENT_EXAM_IDS_STORAGE_KEY = 'recentExamIds';
const MAX_RECENT_EXAM_IDS = 3;

export const getStoredRecentExamIds = () => {
	if (typeof window === 'undefined') return [];

	try {
		const storedValue = window.localStorage.getItem(RECENT_EXAM_IDS_STORAGE_KEY);
		if (!storedValue) return [];

		const parsedValue = JSON.parse(storedValue);
		return Array.isArray(parsedValue) ? parsedValue.filter((id): id is string => typeof id === 'string') : [];
	} catch {
		return [];
	}
};

export const addRecentExamId = (recentExamIds: string[] = [], examId?: string, limit = MAX_RECENT_EXAM_IDS) => {
	if (!examId) return recentExamIds;

	const uniqueIds = recentExamIds.filter((id) => id && id !== examId);
	return [examId, ...uniqueIds].slice(0, limit);
};

const persistRecentExamIds = (recentExamIds: string[]) => {
	if (typeof window === 'undefined') return;

	window.localStorage.setItem(RECENT_EXAM_IDS_STORAGE_KEY, JSON.stringify(recentExamIds));
};

export const buildExamFilters = (
	filters: { tags: string[]; difficultyLevels: ExamDifficultyLevelView[] } = { tags: [], difficultyLevels: [] }
) => ({
	difficultyLevel: filters.difficultyLevels ?? [],
	tags: filters.tags ?? [],
});

export const getNextExamId = (currentExamId?: string, availableExamIds: string[] = []) => {
	if (!currentExamId || !availableExamIds.length) return undefined;

	const currentIndex = availableExamIds.indexOf(currentExamId);
	if (currentIndex === -1) return availableExamIds[0];

	return availableExamIds[(currentIndex + 1) % availableExamIds.length];
};

export const useGame = (
	scrollToBottom: () => void,
	id?: string,
	onSubmit?: () => void,
	filters: { tags: string[]; difficultyLevels: ExamDifficultyLevelView[] } = { tags: [], difficultyLevels: [] },
	isLessonMode = false
) => {
	const { isUserLoggedIn } = useIsUserLoggedIn();
	console.log('id', id);

	const [userBids, setUserBids] = useState<BiddingView[]>([]);
	const [lastUserBid, setLastUserBid] = useState<BiddingView>({} as BiddingView);
	const [bestAnswers, setBestAnswers] = useState<BiddingView[]>([]);
	const [result, setResult] = useState<IResult>(null);
	const [recentBids, setRecentBids] = useState<BiddingView[]>([]);
	const [resultSaveVersion, setResultSaveVersion] = useState(0);
	const [chosenLevels, setChosenLevels] = useState<ExamDifficultyLevelView[]>([]);
	const [isInitiated, setIsInitiated] = useState(false);
	const [recentExamIds, setRecentExamIds] = useState<string[]>(() => getStoredRecentExamIds());

	const { data: chosenGroupData } = useLastGroupQuery({
		skip: !isUserLoggedIn,
	});
	console.log('chosenGroupData', chosenGroupData?.me.recentlyUsedGroupId, isUserLoggedIn);

	const chosenGroup = useMemo(() => {
		return chosenGroupData?.me?.recentlyUsedGroupId || GENERAL_GROUP_NAME;
	}, [chosenGroupData, isUserLoggedIn]);

	const selectedTags = filters?.tags ?? [];
	const selectedDifficultyLevels = filters?.difficultyLevels ?? [];

	const activeExamFilters = useMemo(
		() => buildExamFilters({ tags: selectedTags, difficultyLevels: selectedDifficultyLevels }),
		[selectedDifficultyLevels, selectedTags]
	);

	const handleRecentBids = (bids: BiddingView[]) => {
		setRecentBids(bids);
	};
	const [examResultsSaveMutation] = useExamResultsSaveMutation({
		refetchQueries: [MeDocument, ScoreDocument, GetPublicLessonWithHomeworkBySlugDocument],
	});
	const [getExam, { data: testData, loading: testLoading, error: testError }] = useExamFromGroupLazyQuery({
		fetchPolicy: 'no-cache',
	});

	const [getPublicExam, { data: publicTestData, loading: publicTestLoading, error: publicTestError }] =
		usePublicGroupsLazyQuery({
			fetchPolicy: 'no-cache',
		});

	const randomTest = isUserLoggedIn ? testData?.group?.exam : publicTestData?.publicGroups[0]?.exam;

	const fetchExam = isUserLoggedIn ? getExam : getPublicExam;
	const loading = isUserLoggedIn ? testLoading : publicTestLoading;
	const error = isUserLoggedIn ? testError : publicTestError;

	useEffect(() => {
		if (isInitiated && randomTest?.id && !isLessonMode) return;
		id ? drawTestById(id) : drawTest();
		setIsInitiated(true);
	}, [chosenGroup, id]);

	const reset = () => {
		setUserBids([]);
		setLastUserBid({} as BiddingView);
		setBestAnswers([]);
		setResult(null);
	};

	useEffect(() => {
		if (!userBids.length) return;
		setLastUserBid(userBids.slice(-1)[0]);
	}, [userBids]);

	useEffect(() => {
		if (!randomTest?.id) return;
		setRecentExamIds((prev) => {
			const nextRecentExamIds = addRecentExamId(prev, randomTest.id, MAX_RECENT_EXAM_IDS);
			persistRecentExamIds(nextRecentExamIds);
			return nextRecentExamIds;
		});
	}, [randomTest?.id]);

	const handleResult = (r: IResult) => {
		setResult(r);
	};

	const drawTest = () => {
		reset();
		fetchExam({
			variables: {
				code: chosenGroup,
				input: {
					random: {
						filters: {
							difficultyLevel: activeExamFilters.difficultyLevel,
							tags: activeExamFilters.tags,
							ignoreExamsConnectedToLessons: false,
						},
						deprioritizedExamIds: recentExamIds,
					},
				},
			},
		});
	};

	const repeatTest = () => {
		reset();
		fetchExam({
			variables: {
				code: chosenGroup,
				input: {
					id: randomTest?.id,
				},
			},
		});
	};

	const drawTestById = (id: string) => {
		reset();
		fetchExam({
			variables: {
				code: chosenGroup,
				input: {
					id: id,
				},
			},
		});
		scrollToBottom();
	};

	const handleNextTest = () => {
		reset();
		fetchExam({
			variables: {
				code: chosenGroup,
				input: {
					next: {
						filters: {
							difficultyLevel: activeExamFilters.difficultyLevel,
							tags: activeExamFilters.tags,
							ignoreExamsConnectedToLessons: false,
						},
						startFromId: randomTest?.id,
					},
				},
			},
		});
	};

	const sendResultToBackend = (b) => {
		examResultsSaveMutation({
			variables: {
				input: {
					examId: randomTest.id,
					answers: [...userBids, { ...b }],
					groupCode: chosenGroup,
					revisionId: randomTest.revisionId,
				},
			},
			refetchQueries: [ScoreDocument],
		}).then(() => {
			setResultSaveVersion((prev) => prev + 1);
			onSubmit && onSubmit();
		});
	};

	const handleLevelChange = (level: ExamDifficultyLevelView) => {
		setChosenLevels((prev) => {
			if (prev.includes(level)) {
				return prev.filter((l) => l !== level);
			} else {
				return [...prev, level];
			}
		});
	};

	const southCards = useMemo(() => {
		return randomTest?.definition?.cards ?? [];
	}, [randomTest?.definition?.cards]);

	const handleUserBids = (b: BiddingView) => {
		if (
			compareTwoBids(b, randomTest.definition.answers[userBids.length].halfAnswers[0]) ||
			compareTwoBids(b, randomTest.definition.answers[userBids.length].halfAnswers[1])
		) {
			setResult('HALF');
			setBestAnswers(randomTest.definition.answers[userBids.length].matchingAnswers);
			isUserLoggedIn && sendResultToBackend(b);
		} else if (compareTwoBids(b, randomTest.definition.answers[userBids.length].matchingAnswers[0])) {
			if (userBids.length === randomTest.definition.answers.length - 1) {
				setResult('FULL');
				isUserLoggedIn && sendResultToBackend(b);
			}
		} else if (compareTwoBids(b, randomTest.definition.answers[userBids.length].matchingAnswers[1])) {
			setResult('FULL');
			setBestAnswers(randomTest.definition.answers[userBids.length].matchingAnswers);
			isUserLoggedIn && sendResultToBackend(b);
		} else {
			setBestAnswers(randomTest.definition.answers[userBids.length].matchingAnswers);
			if (compareTwoBidsWithoutAlert(b, randomTest.definition.answers[userBids.length].matchingAnswers[0])) {
				setResult('MISSING_ALERT');
			} else {
				setResult('BAD');
			}
			isUserLoggedIn && sendResultToBackend(b);
		}
		setUserBids((prev) => [...prev, { ...b }]);
	};

	return {
		testError: error,
		randomTest,
		testLoading: loading,
		chosenGroup,
		handleResult,
		userBids,
		handleRecentBids,
		recentBids,
		drawTest,
		drawTestById,
		handleUserBids,
		lastUserBid,
		bestAnswers,
		handleNextTest,
		repeatTest,
		southCards,
		result,
		handleLevelChange,
		chosenLevels,
		resultSaveVersion,
	};
};
